import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface DropdownMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  children: React.ReactNode;
  align?: 'left' | 'right';
  minWidth?: string;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  open,
  onClose,
  anchorRef,
  children,
  align = 'left',
  minWidth = '200px',
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: align === 'right' ? rect.right : rect.left,
    });
  }, [anchorRef, align]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  let top = position?.top;
  let left = position?.left;
  if ((top == null || left == null) && anchorRef.current) {
    const rect = anchorRef.current.getBoundingClientRect();
    top = rect.bottom + 4;
    left = align === 'right' ? rect.right : rect.left;
  }
  if (top == null || left == null) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[20000] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
      style={{
        top,
        minWidth,
        ...(align === 'right'
          ? { right: window.innerWidth - left }
          : { left }),
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export const MenuItem: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  destructive?: boolean;
  disabled?: boolean;
}> = ({ onClick, children, destructive, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full px-4 py-2 text-left text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      destructive
        ? 'text-red-600 hover:bg-red-50 disabled:hover:bg-transparent'
        : 'text-gray-700 hover:bg-gray-50 disabled:hover:bg-transparent'
    }`}
  >
    {children}
  </button>
);

export default DropdownMenu;
